import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { AccountService } from '~/services/domain/AccountService';
import { FeatureAllowanceResponse } from '~/services/domain/AccountTypes';
import { PaywallFeature } from '~/models/Paywall';

interface FeaturedPhotoWarningProps {
  selectedImage: string | File | null;
}

export function FeaturedPhotoWarning({ selectedImage }: FeaturedPhotoWarningProps) {
  const { t } = useTranslation();
  const [featuredPhotoAllowance, setFeaturedPhotoAllowance] = useState<FeatureAllowanceResponse | null>(null);
  const [isCheckingAllowance, setIsCheckingAllowance] = useState(false);

  // Check featured photos allowance when image is selected
  useEffect(() => {
    if (selectedImage) {
      checkFeaturedPhotoAllowance();
    } else {
      setFeaturedPhotoAllowance(null);
    }
  }, [selectedImage]);

  const checkFeaturedPhotoAllowance = async () => {
    if (!selectedImage) return;

    setIsCheckingAllowance(true);
    try {
      const allowance = await AccountService.getFeatureAllowance(PaywallFeature.FeaturedPhotos);
      setFeaturedPhotoAllowance(allowance);
    } catch (error) {
      console.error('Failed to check featured photo allowance:', error);
      // Don't show error to user, just log it
    } finally {
      setIsCheckingAllowance(false);
    }
  };

  const getMinutesUntilReset = (nextAllowedAt: Date | string): number => {
    // Ensure we're working with UTC time since API responses are in UTC
    const resetTime = typeof nextAllowedAt === 'string' 
      ? new Date(nextAllowedAt.endsWith('Z') ? nextAllowedAt : `${nextAllowedAt}Z`)
      : nextAllowedAt;
    const now = new Date();
    const diffMs = resetTime.getTime() - now.getTime();
    const diffMinutes = Math.ceil(diffMs / (1000 * 60));
    return Math.max(0, diffMinutes);
  };

  // Don't render anything if no image, still checking, photo will be featured, or no reset time
  if (!selectedImage || !featuredPhotoAllowance || isCheckingAllowance || featuredPhotoAllowance.IsAllowed) {
    return null;
  }

  const minutesUntilReset = getMinutesUntilReset(featuredPhotoAllowance.NextAllowedAt);

  // Only show warning if there's a reset time
  if (minutesUntilReset <= 0) {
    return null;
  }

  return (
    <VStack className="gap-1 rounded-lg bg-background-100 p-3">
      <Text size="sm" className="text-typography-600">
        {t('post.featuredPhoto.warningWithReset', { minutes: minutesUntilReset })}
      </Text>
    </VStack>
  );
} 